import React from 'react';
import { useQuery } from 'react-query';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Work as WorkIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { jobsAPI, apiUsageAPI } from '../services/api';

const StatCard = ({ title, value, icon, color = 'primary.main' }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" gutterBottom variant="h6">
            {title}
          </Typography>
          <Typography variant="h4" component="div">
            {value}
          </Typography>
        </Box>
        <Box sx={{ color }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { data: jobsData, isLoading: jobsLoading, error: jobsError } = useQuery(
    'jobs',
    () => jobsAPI.getJobs({ limit: 1000 })
  );

  const { data: usageData, isLoading: usageLoading, error: usageError } = useQuery(
    'apiUsage',
    () => apiUsageAPI.getUsageSummary()
  );

  if (jobsLoading || usageLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (jobsError || usageError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load dashboard data
      </Alert>
    );
  }

  const jobs = jobsData || [];
  const totalJobs = jobs.length;
  const completedJobs = jobs.filter(job => job.status && job.status.toLowerCase() === 'completed').length;
  const failedJobs = jobs.filter(job => job.status && job.status.toLowerCase() === 'failed').length;
  const pendingJobs = jobs.filter(job => job.status && job.status.toLowerCase() === 'pending').length;
  const processingJobs = jobs.filter(job => job.status && job.status.toLowerCase() === 'processing').length;

  const totalCost = usageData?.total_cost_cents || 0;
  const totalCalls = usageData?.total_calls || 0;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Jobs"
            value={totalJobs}
            icon={<WorkIcon sx={{ fontSize: 40 }} />}
            color="primary.main"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completed"
            value={completedJobs}
            icon={<CheckCircleIcon sx={{ fontSize: 40 }} />}
            color="success.main"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Failed"
            value={failedJobs}
            icon={<ErrorIcon sx={{ fontSize: 40 }} />}
            color="error.main"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending"
            value={pendingJobs + processingJobs}
            icon={<PendingIcon sx={{ fontSize: 40 }} />}
            color="warning.main"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="API Calls"
            value={totalCalls.toLocaleString()}
            icon={<WorkIcon sx={{ fontSize: 40 }} />}
            color="info.main"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Cost"
            value={`$${(totalCost / 100).toFixed(2)}`}
            icon={<MoneyIcon sx={{ fontSize: 40 }} />}
            color="success.main"
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Recent Jobs
        </Typography>
        {jobs.slice(0, 5).map((job) => (
          <Card key={job.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6">{job.name}</Typography>
                  <Typography color="textSecondary">{job.description}</Typography>
                  <Typography variant="body2">
                    Created: {new Date(job.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: job.status === 'completed' ? 'success.main' :
                             job.status === 'failed' ? 'error.main' :
                             job.status === 'processing' ? 'warning.main' : 'text.secondary'
                    }}
                  >
                    {job.status.toUpperCase()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default Dashboard;
